import React, { useState, useEffect } from "react";
import { useQueryParams, navigate } from "raviger";
import { toast } from 'react-toastify';
import { useDownloadS3File } from "../../../backend/shared";
import { downloadFileContent } from "../../../utils/common";
import { ERROR_MESSAGES } from "../../../utils/constants";
import { hasUserAccess } from "../../../backend/auth";
import { USER_PRIVILEGES } from "../UserManagement/Roles/RoleConfiguration";
import PageLoader from "../../shared/PageLoader";
import { Button } from "react-bootstrap";
import logo from '../../../assets/img/header-logo.png';
import "./S3FileDownload.css";

function S3FileDownload() {
    const [query] = useQueryParams();
    const [downloading, setDownloading] = useState(false);
    const [params, setParams] = useState<any>(null);
    const [unauthorized, setUnauthorized] = useState(false);

    // Check if user has required privileges
    // useEffect(() => {
    //     const hasReviewerPrivilege = hasUserAccess(USER_PRIVILEGES.REVIEWER_VENDOR_ACTIVITIES_DOWNLOAD_BULK_REPORT);
    //     const hasSubmitterPrivilege = hasUserAccess(USER_PRIVILEGES.SUBMITTER_VENDOR_ACTIVITIES_DOWNLOAD_BULK_REPORT);

    //     if (!hasReviewerPrivilege && !hasSubmitterPrivilege) {
    //         setUnauthorized(true);
    //         setTimeout(() => {
    //             navigate("/");
    //         }, 3000);
    //     }
    // }, []);

    const { downloadS3File, isLoading } = useDownloadS3File(
        (response: any) => {
            if (response) {
                const s3Key = (params || {}).s3_key || '';
                const filename = s3Key.split('/').pop() || 'Bulk_report';
                const authorized_user = (params || {}).authorized_user || '';
                downloadFileContent({
                    name: filename,
                    type: response.headers["content-type"],
                    content: response.data,
                });
                toast.success("File downloaded successfully!");
                setDownloading(false);
            } else {
                toast.error(response.message)
            }
        },
        async (error: any) => {
            console.log(error);
            let errorMessage = ERROR_MESSAGES.DEFAULT;
            if (error && error.response && error.response.data) {
                const responseData = error.response.data;
                if (responseData instanceof Blob) {
                    try {
                        const text = await responseData.text();
                        const jsonData = JSON.parse(text);
                        if (jsonData.message) {
                            errorMessage = jsonData.message;
                        }
                    } catch (e) {
                        console.error("Error parsing blob response:", e);
                    }
                } else if (responseData.message) {
                    errorMessage = responseData.message;
                }
            }
            toast.error(errorMessage);
            setDownloading(false);
        }
    );

    useEffect(() => {
        const {
            s3_key,
            s3_bucket,
            s3_region,
            authorized_user,
            company,
            associate_company,
            audit_month,
            vendor_category,
            vendor
        } = query;

        if (s3_key && s3_bucket && s3_region) {
            setParams({
                s3_key,
                s3_bucket,
                s3_region,
                authorized_user,
                company: company || '',
                associate_company: associate_company || '',
                audit_month: audit_month || '',
                vendor_category: vendor_category || '',
                vendor: vendor || ''
            });
        } else {
            toast.error("Invalid download link. Missing required parameters.");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only once on mount since query params are from URL

    function handleDownload() {
        if (params) {
            setDownloading(true);
            const downloadParams: any = {
                s3_key: params.s3_key,
                s3_bucket: params.s3_bucket,
                s3_region: params.s3_region,
            };
            if (params.authorized_user) {
                downloadParams.authorized_user = params.authorized_user;
            }

            downloadS3File(downloadParams);
        } else {
            toast.error("Invalid download parameters");
        }
    }

    // Show unauthorized message if user doesn't have permission
    if (unauthorized) {
        return (
            <>
                {/* Header with Logo */}
                <div className='container-full bg-white header-navbar-container shadow'>
                    <nav className="navbar navbar-expand-lg border-bottom py-1">
                        <div className="container-fluid">
                            <div className="navbar-brand p-0"><img src={logo} alt="Logo" width="140" /></div>
                        </div>
                    </nav>
                </div>

                {/* Unauthorized Message */}
                <div className="s3-file-download-container">
                    <div className="card shadow border-0 p-5 m-4 text-center" style={{ maxWidth: '600px' }}>
                        <h4 className="text-danger mb-3">Access Denied</h4>
                        <p className="mb-0" style={{ fontSize: '16px', color: '#666' }}>
                            You don't have permission to access this page. Redirecting to home page...
                        </p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {/* Header with Logo */}
            <div className='container-full bg-white header-navbar-container shadow'>
                <nav className="navbar navbar-expand-lg border-bottom py-1">
                    <div className="container-fluid">
                        <div className="navbar-brand p-0"><img src={logo} alt="Logo" width="140" /></div>
                    </div>
                </nav>
            </div>

            {/* Download Content */}
            <div className="s3-file-download-container">
                {params ? (
                    <div className="card shadow border-0 p-4 m-4" style={{ maxWidth: '800px' }}>
                        <h5 className="mb-4" style={{ color: '#666', fontWeight: 400, fontSize: '24px' }}>
                            Please click on 'Download' button to download all the reports
                        </h5>

                        <div className="download-info mb-4">
                            {params.company && (
                                <div className="info-row">
                                    <span className="info-label">Company</span>
                                    <span className="info-value">{params.company}</span>
                                </div>
                            )}
                            {params.associate_company && (
                                <div className="info-row">
                                    <span className="info-label">Associate Company</span>
                                    <span className="info-value">{params.associate_company}</span>
                                </div>
                            )}
                            {params.audit_month && (
                                <div className="info-row">
                                    <span className="info-label">Audit Month & Year</span>
                                    <span className="info-value">{params.audit_month}</span>
                                </div>
                            )}
                            {params.vendor_category && (
                                <div className="info-row">
                                    <span className="info-label">Vendor Category</span>
                                    <span className="info-value">{params.vendor_category}</span>
                                </div>
                            )}
                            {params.vendor && (
                                <div className="info-row">
                                    <span className="info-label">Vendor</span>
                                    <span className="info-value">{params.vendor}</span>
                                </div>
                            )}
                        </div>

                        <div className="download-button-container">
                            <Button
                                variant="primary"
                                onClick={handleDownload}
                                disabled={downloading || isLoading}
                                className="download-button"
                            >
                                {downloading || isLoading ? "Downloading..." : "Download"}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="card shadow border-0 p-4 m-4">
                        <p className="text-danger mb-0" style={{fontSize: "24px"}}>
                            Invalid download link. Please check your URL and try again.
                        </p>
                    </div>
                )}
            </div>
            {(downloading || isLoading) && <PageLoader />}
        </>
    );
}

export default S3FileDownload;
